import { OrdenPrestador } from 'src/app/models/OrdenPrestador';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { API_URI } from 'src/environments/environment';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-order-detail-prestador',
    templateUrl: './order-detail-prestador.component.html',
    styleUrls: ['./order-detail-prestador.component.css'],
    standalone: false
})
export class OrderDetailPrestadorComponent {
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _aRoute = inject(ActivatedRoute);
  
  error = signal<string>('');
  loadingGlobal = computed(() => this._loadingService.isLoading());
  loadingLocal = signal<boolean>(false);

  id: number = Number(this._aRoute.snapshot.paramMap.get('id'));
  ordenPrestador?: OrdenPrestador;

  preview_img_avatar_cliente?: string;
  preview_img_avatar_prestador?: string;

  url_perfil_cliente?: string;
  url_perfil_prestador?: string;

  id_perfil_usuario: number = 0;
  apiUri: string = API_URI;
 
   async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        if (this._authService.session() === 'guest') {
          this._router.navigate(['/sign-in']);
          return;
        }

        if (!this.id) {
          this._router.navigate(['/dashboard']);
          return;
        }

        this.id_perfil_usuario = this._usuarioService.usuario().id_perfil ?? 0;

        const resOrden = await firstValueFrom(this._orderPrestadorService.getOrdenPrestador(this.id));
        if (!resOrden) {
          this._router.navigate(['/dashboard']);
          return;
        }

        this.ordenPrestador = resOrden;

        this.url_perfil_cliente = '/perfil-cliente/' + this.ordenPrestador.id_cliente!;
        this.preview_img_avatar_cliente = this.ordenPrestador.imgAvatarCliente != null
          ? API_URI + 'uploads/images/small-' + this.ordenPrestador.imgAvatarCliente
          : './assets/img/profesional-init.png';

        this.url_perfil_prestador = '/perfil-prestador/' + this.ordenPrestador.id_prestador!;
        this.preview_img_avatar_prestador = this.ordenPrestador.imgAvatarPrestador != null
          ? API_URI + 'uploads/images/small-' + this.ordenPrestador.imgAvatarPrestador
          : './assets/img/profesional-init.png';
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar el detalle del turno.');
      }
    });
  }

  async completadoOrderPrestador(): Promise<void> {
    this.error.set('');
    this.loadingLocal.set(true);

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._orderPrestadorService.updateEstadoOrdenPrestador(this.id, 4)); // COMPLETADO
        this._router.navigate(['/dashboard']);
      });
    } catch (e: any) {
      console.error('Error al actualizar el Estado de la Orden de Prestador:', e);
      this.error.set('No se pudo actualizar el estado del turno.');
    } finally {
      this.loadingLocal.set(false);
    }
  }

  back(): void {
    this._location.back();
  }

  openFile(): void {
    const x_img_receta = this.ordenPrestador?.x_img_receta;
    if (!x_img_receta) return;
    window.open(`${this.apiUri}uploads/images/${x_img_receta}`, '_blank');
  }

   descargarICS(): void {
    const fechaInicio = this.ordenPrestador?.d_fecha_turno ?? '';
    const fechaFin: Date | '' = this.ordenPrestador?.d_fecha_turno ? new Date(fechaInicio) : '';

    if (fechaFin instanceof Date) {
      fechaFin.setHours(fechaFin.getHours() + 1);
    }

    const formatoICS = (fecha: Date | '') => {
      if (fecha instanceof Date) {
        return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
      return '';
    };

    const evento = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:LibreSalud
BEGIN:VEVENT
UID:12345@example.com
DTSTAMP:${formatoICS(new Date())}
DTSTART:${formatoICS(fechaInicio)}
DTEND:${formatoICS(fechaFin)}
SUMMARY:Turno con ${this.ordenPrestador?.nombrePrestador}
DESCRIPTION:${this.ordenPrestador?.nombreEspecialidad} - ${this.ordenPrestador?.nombrePrestacion}.
LOCATION:${this.ordenPrestador?.x_zona ?? ''}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([evento], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);

    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = 'evento.ics';
    enlace.click();

    URL.revokeObjectURL(url);
  }

  agregarAGoogleCalendar(): void {
    const titulo = 'Turno con ' + this.ordenPrestador?.nombrePrestador;
    const descripcion = (this.ordenPrestador?.nombreEspecialidad ?? '') + ' - ' + (this.ordenPrestador?.nombrePrestacion ?? '');
    const ubicacion = this.ordenPrestador?.x_zona ?? '';
    const fechaInicio = this.ordenPrestador?.d_fecha_turno ?? '';
    const fechaFin = this.ordenPrestador?.d_fecha_turno ? new Date(fechaInicio) : '';

    if (fechaFin instanceof Date) {
      fechaFin.setHours(fechaFin.getHours() + 1);
    }

    const formatoICS = (fecha: Date | '') => {
      if (fecha instanceof Date) {
        return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      }
      return '';
    };

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(titulo)}&dates=${formatoICS(fechaInicio)}/${formatoICS(fechaFin)}&details=${encodeURIComponent(descripcion)}&location=${encodeURIComponent(ubicacion)}&sf=true&output=xml`;

    window.open(url, '_blank');
  }

  agregarAOutlook(): void {
    const titulo = 'Turno con ' + this.ordenPrestador?.nombrePrestador;
    const descripcion = (this.ordenPrestador?.nombreEspecialidad ?? '') + ' - ' + (this.ordenPrestador?.nombrePrestacion ?? '');
    const ubicacion = this.ordenPrestador?.x_zona ?? '';
    const fechaInicio = this.ordenPrestador?.d_fecha_turno ?? '';
    const fechaFin = this.ordenPrestador?.d_fecha_turno ? new Date(fechaInicio) : '';

    if (fechaFin instanceof Date) {
      fechaFin.setHours(fechaFin.getHours() + 1);
    }

    const url = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&startdt=${encodeURIComponent(fechaInicio instanceof Date ? fechaInicio.toISOString() : '')}&enddt=${encodeURIComponent(fechaFin instanceof Date ? fechaFin.toISOString() : '')}&subject=${encodeURIComponent(titulo)}&body=${encodeURIComponent(descripcion)}&location=${encodeURIComponent(ubicacion)}`;

    window.open(url, '_blank');
  }

  get estadoOrdenId(): number {
    return this.ordenPrestador?.id_estado_orden ?? 0;
  }

  get puedeAgregarCalendario(): boolean {
    return this.estadoOrdenId === 3 || this.estadoOrdenId === 4 || this.estadoOrdenId === 6 || this.estadoOrdenId === 9;
  }

  get textoPago(): string {
    switch (this.estadoOrdenId) {
      case 3: // Aceptado
        return 'Abonado con MP';
      case 4: // Completado
        return 'Abonado con MP';
      case 6: // Pago Aprobado
        return 'Abonado con MP';
      case 7: // Procesando Pago
        return 'Pago en proceso';
      case 8: // Pago Rechazado
        return 'Pago Rechazado';
      case 9: // Calificado
        return 'Abonado con MP';
      case 11: // Pago Cancelado
        return 'Pago Cancelado';
      default:
        return 'Pago pendiente';
    }
  }

  get textoZona(): string {
    const zona = this.ordenPrestador?.x_zona?.trim();
    return zona ? zona : 'Dirección a confirmar con el prestador';
  }
}
